/* eslint-disable unused-imports/no-unused-vars */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  getInventoryEmptyGroupLabel,
  mapInventoryEmptyGroupFilterCriteria,
  ProjectGroupPermission,
  ProjectIssuanceConstants,
  UserType,
} from "../../../../constants";
import { CursorChangeProps, ResultType, SelectData } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { getAggregateIssuances } from "../../../../service/query/QueryService";
import { ResultData, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import {
  buildNutrientStackedChartData,
  flattenObject,
  getInventoryKeyColours,
  getInventoryUnitSuffix,
  getInventoryViewButtons,
  getNutrientDimensionColumns,
  getNutrientFromRow,
  INVENTORY_AREA_HA_COLUMN,
  INVENTORY_GROUP_BY_COLUMN_1,
  INVENTORY_GROUP_BY_COLUMN_2,
  INVENTORY_GROUP_BY_COLUMN_3,
  INVENTORY_UNIT_COLUMNS,
  NutrientChartSeries,
  NutrientStackedChartData,
  withInventoryUnitLabel,
} from "../../../../utils";
import { getProjectDetailsByUuid } from "../../../../utils/routes";
import {
  ChartDatasets,
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
  StackedChartData,
  Toast,
} from "../../../../widget";
import { InventoryManagementBoardPageData } from "../../inventory/inventory-management-board/useInventoryManagementBoard";
import { LandholdingContext } from "../LandholdingContext";

interface UseInventoryManagementBoardReturnData {
  gridOnChange: DataGridCursorDataLoadEventHandler;
  isReadableProject: boolean;
  hasData: boolean;
  hasEnoughDataForScrolling: boolean;
  isLoading: boolean;
  selectedUnitMeasurementData: string;
  activeSelectOptionText: string;
  handleChangeSelectOption: (value: string) => void;
  inventorySelectorButtons: string[];
  pageData: InventoryTabData;
  /** D-4 - " kg/yr" on Nutrient Neutrality only, appended to the y-axis ticks and the tooltip. */
  suffix?: string;
  columns: DataGridColumnDefinition[];
  tagsFilterData: SelectData;
  onTagsFilter: (keys: string[]) => Promise<void>;
  tagsFilterSelectedData?: string;
  currentUserType?: UserType;
  /** The statuses applied, from LandholdingContext — the dashboard KPI above owns the control. */
  selectedStatuses: string[];
}

export interface InventoryTabData {
  chartData: StackedChartData[] | undefined;
  chartDatasets: ChartDatasets | undefined;
  chartLabels: (string | string[])[];
  /** Nutrient Neutrality only - the nutrients the bars are coloured by, so the chart key mirrors them. */
  chartKeyNutrients?: string[];
}

/** What buildLandholdingChartData returns: the bars, their labels, and the nutrients drawn. */
export type LandholdingChartData = NutrientStackedChartData;

/** Fixed key so a repeatedly failing fetch replaces its own toast rather than stacking. */
const INVENTORY_LOAD_ERROR_TOAST_KEY = "landholding-inventory-tab-load-error";

const {
  ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY: BIODIVERSITY,
  ISSUANCE_UNIT_MEASUREMENT_CARBON: CARBON,
  ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY: NUTRIENT_NEUTRALITY,
  ISSUANCE_UNIT_MEASUREMENT_RESTORATION: RESTORATION,
} = ProjectIssuanceConstants;

/**
 * The view buttons this surface offers for a measurement.
 *
 * The switch itself now lives in `getInventoryViewButtons`, shared with the inventory management
 * board. "" (Overall), Ecosystem recovery and any unrecognised measurement still fall to NO views
 * here, where the board falls to its three Overall views - that difference is the shared helper's
 * `surface` parameter and is preserved exactly.
 */
export const getLandholdingInventorySelectorButtons = (unitMeasurement: string): string[] =>
  getInventoryViewButtons("landholding", unitMeasurement);

/*
  Common column definitions. The bases are shared with the inventory management board; this surface
  widens slot 1, which the board does not, so that override stays here.
*/
const groupByName1Column: DataGridColumnDefinition = { ...INVENTORY_GROUP_BY_COLUMN_1, minWidth: 140 };

const groupByName2Column: DataGridColumnDefinition = INVENTORY_GROUP_BY_COLUMN_2;

const groupByName3Column: DataGridColumnDefinition = INVENTORY_GROUP_BY_COLUMN_3;

const sumAreaHaColumn: DataGridColumnDefinition = INVENTORY_AREA_HA_COLUMN;

/** The group-by bases this surface passes to the shared Nutrient Neutrality column set. */
const landholdingGroupByColumns = {
  name1: groupByName1Column,
  name2: groupByName2Column,
  name3: groupByName3Column,
};

/*
  A viewer who sees the full figures gets the same five unit columns the board renders. A viewer
  without them gets this surface's own Allocated/Available pair, which no other surface has.

  D-4 - both branches are quantities, so both carry the measurement's unit: "Available (kg/yr)" on
  Nutrient Neutrality, and the bare headers on every other measurement.
*/
export const getLandholdingCommonColumns = (
  isReadableProject: boolean,
  unitMeasurement: string
): DataGridColumnDefinition[] =>
  withInventoryUnitLabel(
    isReadableProject
      ? INVENTORY_UNIT_COLUMNS
      : [
          { name: "Allocated", key: "allocatedUnits", dataType: "number", filterable: false, sortable: false },
          { name: "Available", key: "availableUnits", dataType: "number", filterable: true, sortable: true },
        ],
    unitMeasurement
  );

/*
  Keyed `${measurement}|${groupBy field}`. The inventory management board keys the same idea on the
  selector label instead ("Carbon|By vintage"), so the two vocabularies are not interchangeable.
*/
export const LANDHOLDING_COLUMNS_DICT: Record<string, DataGridColumnDefinition[]> = {
  "|projectNameMeasurement": [
    { ...groupByName1Column, key: "link", name: "Project", formatter: "link" },
    { ...groupByName3Column, name: "Measurement" },
  ],
  [`${CARBON}|projectName`]: [{ ...groupByName1Column, key: "link", name: "Name", formatter: "link" }],
  [`${CARBON}|vintageStartDate`]: [{ ...groupByName1Column, name: "Vintage" }],
  [`${CARBON}|projectType`]: [{ ...groupByName1Column, name: "Project type" }],
  [`${CARBON}|quantityType`]: [{ ...groupByName1Column, name: "Quantity type" }],
  [`${BIODIVERSITY}|projectName`]: [{ ...groupByName1Column, key: "link", name: "Name", formatter: "link" }],
  // Restoration's only view (KAN-9934). Its chart colours come from getInventoryKeyColours below.
  [`${RESTORATION}|projectName`]: [{ ...groupByName1Column, key: "link", name: "Name", formatter: "link" }],
  [`${BIODIVERSITY}|habitat`]: [
    { ...groupByName1Column, name: "Habitat group" },
    { ...groupByName2Column, name: "Distinctiveness" },
    sumAreaHaColumn,
  ],
  [`${BIODIVERSITY}|habitatCondition`]: [{ ...groupByName1Column, name: "Habitat condition" }],
  [`${BIODIVERSITY}|lpa`]: [{ ...groupByName1Column, name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.lpa }],
  [`${BIODIVERSITY}|nca`]: [{ ...groupByName1Column, name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.nca }],
  [`${BIODIVERSITY}|lnrs`]: [{ ...groupByName1Column, name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.lnrs }],
  /*
    Nutrient Neutrality. Each entry declares only its DIMENSION columns; landholdingGridGetColumns
    appends getLandholdingCommonColumns, which supplies the availability figures for whichever
    isReadableProject branch is live.

    The five entries themselves come from getNutrientDimensionColumns, shared with the inventory
    management board - which labels the same five views differently but requests the same group-by
    keys, and so renders the same dimension columns from the same slots.

    Deliberately absent: sumAreaHaColumn. Area is a Biodiversity concept and REQ-015 gives Nutrient
    Neutrality no area dimension.
  */
  [`${NUTRIENT_NEUTRALITY}|projectNameQuantityType`]: getNutrientDimensionColumns(
    "projectNameQuantityType",
    landholdingGroupByColumns
  ),
  [`${NUTRIENT_NEUTRALITY}|quantityType`]: getNutrientDimensionColumns("quantityType", landholdingGroupByColumns),
  [`${NUTRIENT_NEUTRALITY}|designatedSiteQuantityType`]: getNutrientDimensionColumns(
    "designatedSiteQuantityType",
    landholdingGroupByColumns
  ),
  [`${NUTRIENT_NEUTRALITY}|lpaQuantityType`]: getNutrientDimensionColumns("lpaQuantityType", landholdingGroupByColumns),
  [`${NUTRIENT_NEUTRALITY}|lnrsQuantityType`]: getNutrientDimensionColumns(
    "lnrsQuantityType",
    landholdingGroupByColumns
  ),
};

export const landholdingGridGetColumns = (
  unitMeasurement: string | undefined | null,
  groupCondition: string | undefined | null,
  isReadableProject: boolean
): DataGridColumnDefinition[] => {
  const dictKey = `${unitMeasurement || ""}|${groupCondition || ""}`;
  return [
    ...(LANDHOLDING_COLUMNS_DICT[dictKey] || []),
    ...getLandholdingCommonColumns(isReadableProject, unitMeasurement || ""),
  ];
};

/*
  Group-by keys deliberately do NOT compose - the server takes exactly one and rejects a second -
  so a view showing a dimension AND the unit type beside it has to ask for a single COMPOSITE key.
  Nutrient Neutrality puts the nutrient next to four of its five dimensions, hence the four
  `...QuantityType` keys, exactly as the inventory management board requests them (KAN-9744).

  "By nutrient" needs no composite: its dimension IS the nutrient, so it reuses `quantityType`.
*/
export const getLandholdingOptionDict = (unitMeasurement: string): { [key: string]: string } => {
  const isNutrientNeutrality =
    unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY;

  /*
    Overall groups by project AND measurement, since its rows span measurements. Nutrient Neutrality
    needs the nutrient beside the project, so it takes the composite key. Everything else groups by
    the project alone.
  */
  const projectNameKey = (): string => {
    if (unitMeasurement === "") return "projectNameMeasurement";
    return isNutrientNeutrality ? "projectNameQuantityType" : "projectName";
  };

  return {
    "By project": projectNameKey(),
    "By nutrient": "quantityType",
    "By designated site": "designatedSiteQuantityType",
    "By habitat group": "habitat",
    "By condition": "habitatCondition",
    "By LPA": isNutrientNeutrality ? "lpaQuantityType" : "lpa",
    "By NCA": "nca",
    "By LNRS": isNutrientNeutrality ? "lnrsQuantityType" : "lnrs",
    "By vintage": "vintageStartDate",
    "By type": "projectType",
    "By unit": "quantityType",
  };
};

export const getLandholdingGroupCondition = (optionText: string, unitMeasurement: string): string => {
  const optionDict = getLandholdingOptionDict(unitMeasurement);
  if (optionDict[optionText] === undefined && unitMeasurement === "") {
    return unitMeasurement === "" ? "projectNameMeasurement" : "projectName";
  }
  return optionDict[optionText];
};

/** The unit series this surface stacks, which differ by whether the viewer sees the full figures. */
const landholdingUnitSeries = (isReadableProject: boolean): readonly NutrientChartSeries[] =>
  isReadableProject
    ? [
        { label: "Available", key: "availableUnits", shade: "light", hover: "lightHover" },
        { label: "Reserved", key: "reservedUnits", shade: "mid", hover: "midHover" },
        { label: "Sold", key: "soldUnits", shade: "dark", hover: "darkHover" },
      ]
    : [
        { label: "Allocated", key: "allocatedUnits", shade: "dark", hover: "darkHover" },
        { label: "Available", key: "availableUnits", shade: "light", hover: "lightHover" },
      ];

/*
  Carbon takes the INDIGO family, Restoration MAGENTA, and every other measurement - Ecosystem
  recovery included - the GREEN one. The colours come from the shared getInventoryKeyColours resolver.

  Nutrient Neutrality is the exception: it colours by NUTRIENT rather than by measurement, because
  four of its five views group by (dimension, nutrient) and so return one row per pair. Drawn a row
  at a time those are two bars with the same label, so they are collapsed into one bar whose stack
  carries both nutrients - the same treatment, from the same shared builder, as the inventory
  management board.
*/
export const buildLandholdingChartData = (
  gridData: ResultData[],
  chartLabels: (string | string[])[],
  isReadableProject: boolean,
  unitMeasurement: string,
  groupCondition: string
): LandholdingChartData => {
  const series = landholdingUnitSeries(isReadableProject);

  if (unitMeasurement === NUTRIENT_NEUTRALITY) {
    return buildNutrientStackedChartData(
      gridData,
      chartLabels,
      series,
      (row) => getNutrientFromRow(groupCondition, row),
      /*
        Only the project views need a second identity: project display names carry no unique
        constraint, so two same-named projects must stay two bars rather than one summed bar.
      */
      groupCondition === "projectNameQuantityType" ? (row) => (row.groupByFieldContent2 as string) ?? "" : undefined
    );
  }

  const colours = getInventoryKeyColours(unitMeasurement);

  return {
    chartLabels,
    chartKeyNutrients: [],
    chartData: series.map((s) => ({
      label: s.label,
      data: gridData.map((item) => item[s.key] as number),
      backgroundColor: colours[s.shade],
      hoverBackgroundColor: colours[s.hover],
    })),
  };
};

export const useInventory = (): UseInventoryManagementBoardReturnData => {
  const { currentUserType } = useAuth();

  const {
    landholdingDetails,
    publicLandholdingDetails,
    readGroupProjectsPermission,
    selectedStatuses,
    selectedUnitMeasurement: selectedUnitMeasurementData,
  } = useContext(LandholdingContext);
  const isReadableProject = !landholdingDetails;

  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [hasEnoughDataForScrolling, setHasEnoughDataForScrolling] = useState(false);
  const [activeSelectOptionText, setActiveSelectOptionText] = useState<string>("By project");
  const [tagsFilterSelectedData, setTagsFilterSelectedData] = useState<string | undefined>();
  const [tagsFilterData, setTagsFilterData] = useState<SelectData>([]);
  const location = useLocation();

  const [pageData, setPageData] = useState<InventoryManagementBoardPageData>({
    chartData: [],
    chartLabels: [],
    chartDatasets: [],
    chartKeyNutrients: [],
  });

  // Switching view remounts the grid, which fires a fresh gridOnChange while the previous
  // request may still be in flight. Only the most recent request may write chart and loading
  // state, otherwise a slow earlier response leaves the chart showing the previous view.
  const chartRequestIdRef = useRef(0);

  // Any view change remounts the grid and starts a new request, so discard whatever is in
  // flight and show the loading state straight away rather than the half-updated chart.
  const startViewChange = (): void => {
    chartRequestIdRef.current += 1;
    setIsLoading(true);
  };

  const handleChangeSelectOption = (value: string): void => {
    if (value === activeSelectOptionText) return;
    startViewChange();
    setActiveSelectOptionText(value);
  };

  useEffect(() => {
    setActiveSelectOptionText("By project");
  }, [selectedUnitMeasurementData]);

  /*
    Derived rather than set by a handler: the measurement is chosen on the KPI card above, so this
    hook never sees the change event - only the new value.
  */
  const inventorySelectorButtons = useMemo(
    () => getLandholdingInventorySelectorButtons(selectedUnitMeasurementData),
    [selectedUnitMeasurementData]
  );

  const formatData = useCallback(
    (responseData: ResultData[]): ResultData[] =>
      responseData.map((el) => {
        const result = flattenObject(el);

        if (activeSelectOptionText === "By project") {
          result.link = <DataGridLinkCellFormatterData>{
            text: el.groupByFieldContent1,
            to: getProjectDetailsByUuid(el.groupByFieldContent2?.toString() || "", currentUserType),
            state: { origin: { goBackText: "Back to inventory", from: location.pathname } as IStateOrigin },
          };
        }
        return result;
      }) || [],
    []
  );

  const gridGetColumns = (
    unitMeasurement: string | undefined | null,
    groupCondition: string | undefined | null
  ): DataGridColumnDefinition[] => landholdingGridGetColumns(unitMeasurement, groupCondition, isReadableProject);

  const getGroupCondition = (optionText: string): string =>
    getLandholdingGroupCondition(optionText, selectedUnitMeasurementData);

  const gridOnChange = async ({ filtering, paging, sorting, isExport }: CursorChangeProps): Promise<ResultType> => {
    /*
      An export fetches the full result set through this same loader while a display load may still be in
      flight. It is tracked by the grid's own request id, so it must not claim this one: doing so would
      make the in-flight display request look stale and return the empty result the grid then renders.
    */
    if (!isExport) chartRequestIdRef.current += 1;
    const requestId = chartRequestIdRef.current;
    const isLatestRequest = (): boolean => isExport === true || requestId === chartRequestIdRef.current;

    if (!isExport) setIsLoading(true);
    const filterCriteria = dataGridMapFilterCriteria(filtering);
    if (sorting.length === 0) {
      sorting.push({ key: "groupByFieldContent1", direction: "asc" });
    }

    const data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCondition = [];
    if (selectedUnitMeasurementData) {
      filterCondition.push({
        filterBy: "measurementType",
        filterOperator: "eq",
        filterValue: selectedUnitMeasurementData,
      });
    }
    if (tagsFilterSelectedData) {
      filterCondition.push({
        filterBy: "tag",
        filterOperator: "in",
        filterValue: `[${tagsFilterSelectedData}]`,
      });
    }

    if (selectedStatuses.length > 0) {
      filterCondition.push({
        filterBy: "issuanceStatus",
        filterOperator: "in",
        filterValue: `[${selectedStatuses.join(",")}]`,
      });
    }

    filterCondition.push({
      filterBy: "project",
      filterOperator: "in",
      filterValue: `[${landholdingDetails ? landholdingDetails.projects.map((p) => p.uuid) : publicLandholdingDetails?.projects.map((p) => p.uuid)}]`,
    });

    if (filterCriteria.link) {
      filterCriteria.groupByFieldContent1 = filterCriteria.link;
      delete filterCriteria.link;
    }

    // The group cell shows an empty LPA/NCA/LNRS as "No LPA", so a filter typed against that label
    // has to be turned back into a null comparison before it reaches the server.
    if (filterCriteria.groupByFieldContent1) {
      filterCriteria.groupByFieldContent1 = mapInventoryEmptyGroupFilterCriteria(
        filterCriteria.groupByFieldContent1,
        activeSelectOptionText
      );
    }

    const sortKeyMap: Record<string, string> = {
      link: "groupByFieldContent1",
    };

    await getAggregateIssuances({
      filters: filterCondition,
      groupBy: [getGroupCondition(activeSelectOptionText)],
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: paging.pageSize,
      },
      isReadableProject: readGroupProjectsPermission !== ProjectGroupPermission.NONE,
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((s: { key: any; direction: any }) => ({
        key: sortKeyMap[s.key] || s.key,
        direction: s.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        if (response.status === Status.Success && response.data && isLatestRequest()) {
          const chartLabelsArray: (string | string[])[] = [];
          const gridData: ResultData[] = [];
          response.data.results.forEach((result) => {
            let nameValue: any = result.groupByFieldContent1 ?? "";
            if (nameValue instanceof Date) {
              nameValue = nameValue.getFullYear().toString();
            }
            if (nameValue === "") {
              nameValue = getInventoryEmptyGroupLabel(activeSelectOptionText) ?? "";
            }

            let chartLabel = nameValue;
            if (activeSelectOptionText === "By habitat group") {
              chartLabel = [result.groupByFieldContent1, result.groupByFieldContent2];
            }

            gridData.push({
              groupByFieldContent1: nameValue,
              groupByFieldContent2: result.groupByFieldContent2,
              groupByFieldContent3: result.groupByFieldContent3,
              availableUnits: Number(result.availableUnits ?? 0),
              reservedUnits: Number(result.reservedUnits ?? 0),
              quotedUnits: Number(result.quotedUnits ?? 0),
              soldUnits: Number(result.soldUnits ?? 0),
              allocatedUnits: Number(result.reservedUnits ?? 0) + Number(result.soldUnits ?? 0),
              percentageSold: Number(result.percentageSold.toFixed(2) ?? 0),
              averagePrice: Number(result.averagePrice ?? 0),
              areaHa: Number(result.areaHa ?? 0),
            });
            chartLabelsArray.push(chartLabel);
          });
          // Only the displayed rows drive the chart - an export loads a different page size.
          if (!isExport) {
            const chart = buildLandholdingChartData(
              gridData,
              chartLabelsArray,
              isReadableProject,
              selectedUnitMeasurementData,
              getLandholdingGroupCondition(activeSelectOptionText, selectedUnitMeasurementData)
            );
            setPageData({
              chartDatasets: [],
              chartData: chart.chartData,
              chartLabels: chart.chartLabels,
              chartKeyNutrients: chart.chartKeyNutrients,
            });
          }
          data.resultData.push(...formatData(gridData));

          data.paging = {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          };

          // Tag filter options and the empty/scroll flags describe the displayed rows only.
          if (!isExport) {
            setTagsFilterData(
              (response.data.aspects.tags || []).map((v) => ({
                key: `${v.tagUuid}`,
                value: JSON.parse(v.tagData)?.name,
              }))
            );
            setHasData(response.data.results.length > 0);
            setHasEnoughDataForScrolling(response.data.results.length >= 5);
          }
        } else if (!isExport && isLatestRequest()) {
          /*
            A rejected request must not leave the previous view's chart on screen. The grid clears
            itself — `data` is only populated on success — but the chart is state, so it has to be
            cleared explicitly.
          */
          setHasData(false);
          setPageData({ chartData: [], chartLabels: [], chartDatasets: [] });
          Toast.error({ key: INVENTORY_LOAD_ERROR_TOAST_KEY, message: "Failed to load inventory" });
        }
      })
      .finally(() => {
        if (!isExport && isLatestRequest()) setIsLoading(false);
      });

    return data;
  };

  const onTagsFilter = async (keys: string[]): Promise<void> => {
    const selected = keys.length > 0 ? keys.join(",") : undefined;

    // Only flag a reload when the grid key actually changes, otherwise nothing remounts
    // and the loading state would never be cleared.
    if (selected !== tagsFilterSelectedData) startViewChange();

    setTagsFilterSelectedData(selected);
  };

  return {
    gridOnChange,
    isReadableProject,
    selectedStatuses,
    isLoading,
    hasData,
    hasEnoughDataForScrolling,
    selectedUnitMeasurementData,
    activeSelectOptionText,
    handleChangeSelectOption,
    inventorySelectorButtons,
    pageData,
    suffix: getInventoryUnitSuffix(selectedUnitMeasurementData),
    columns: gridGetColumns(selectedUnitMeasurementData, getGroupCondition(activeSelectOptionText)),
    tagsFilterData,
    onTagsFilter,
    tagsFilterSelectedData,
    currentUserType,
  };
};
